// TariffsList.jsx
import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faInfoCircle,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import '../../QueueFull/cabinet-zdo-requests.css';
import './style.css';

const TariffsList = () => {
  const [activeTab, setActiveTab] = useState('type');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [copiedItem, setCopiedItem] = useState(null);

  // Данные для справочника (статичные, как памятка)
  const referenceData = {
    // Типы тарифов
    type: [
      {
        id: 1,
        name: 'Постійний',
        code: 'STANDARD',
        description: 'Базовий тариф для звичайного відвідування',
        characteristics: 'Повний день, 5 днів на тиждень',
        usage: 'Основна маса дітей',
        note: 'Найпопулярніший тип тарифу'
      },
      {
        id: 2,
        name: 'Тимчасовий',
        code: 'TEMPORARY',
        description: 'Тариф з обмеженим періодом дії',
        characteristics: 'Як і для постійного типу',
        usage: 'На випадок особливих обставин',
        note: ''
      }
    ],
    
    // Вікові категорії
    age: [
      {
        id: 1,
        ageGroup: '1-3 роки',
        code: 'AGE_1_3',
        description: 'Діти віком від 1 до 3 років',
        characteristics: 'Адаптаційна група, основи самообслуговування',
        educationalProgram: 'Розвиток моторики, мовлення',
        groupSize: 'До 10 дітей',
        note: 'Обов\'язковий денний сон'
      },
      {
        id: 2,
        ageGroup: '2-3 роки',
        code: 'AGE_2_3',
        description: 'Діти віком від 2 до 3 років',
        characteristics: 'Молодша група, соціалізація',
        educationalProgram: 'Розвиток мовлення, творчість',
        groupSize: 'До 15 дітей',
        note: 'Початкові навички самообслуговування'
      },
      {
        id: 3,
        ageGroup: '1-4 роки',
        code: 'AGE_3_4',
        description: 'Діти віком від 1 до 4 років',
        characteristics: 'Різновікова',
        educationalProgram: 'Логіка, математика, читання',
        groupSize: 'До 20 дітей',
        note: 'Інтенсивний розвиток мовлення'
      },
      {
        id: 4,
        ageGroup: '4-6 років',
        code: 'AGE_4_6',
        description: 'Діти віком від 4 до 6 років',
        characteristics: 'Старша група, підготовка до школи',
        educationalProgram: 'Письмо, читання, основи наук',
        groupSize: 'До 20 дітей',
        note: 'Акцент на підготовку до школи'
      }
    ],
    
    // Типи харчування
    nutrition: [
      {
        id: 1,
        name: 'Стандартне',
        code: 'NUTR_STANDARD',
        description: 'Звичайне повноцінне харчування',
        characteristics: '3-разове харчування (сніданок, обід, полуденок)',
        menu: 'Збалансоване меню згідно з нормами',
        restrictions: 'Немає спеціальних обмежень',
        note: 'Підходить для більшості дітей'
      },
      {
        id: 2,
        name: 'Дієтичне',
        code: 'NUTR_DIET',
        description: 'Спеціальне дієтичне харчування',
        characteristics: 'Індивідуальний підбір продуктів',
        menu: 'Враховує медичні показання',
        restrictions: 'За призначенням лікаря',
        note: 'Вимагає медичної довідки'
      }
    ]
  };

  // Колонки для разных вкладок
  const columnsConfig = {
    type: [
      { key: 'name', label: 'Назва типу', sortable: true, width: '15%' },
      { key: 'code', label: 'Код', sortable: true, width: '10%' },
      { key: 'description', label: 'Опис', sortable: true, width: '20%' },
      { key: 'characteristics', label: 'Характеристики', sortable: true, width: '20%' },
      { key: 'usage', label: 'Застосування', sortable: true, width: '20%' },
      { key: 'note', label: 'Примітка', sortable: true, width: '10%' },
      { key: 'actions', label: 'Дії', sortable: false, width: '5%' }
    ],
    age: [
      { key: 'ageGroup', label: 'Вікова категорія', sortable: true, width: '12%' },
      { key: 'code', label: 'Код', sortable: true, width: '10%' },
      { key: 'description', label: 'Опис', sortable: true, width: '18%' },
      { key: 'characteristics', label: 'Характеристики', sortable: true, width: '15%' },
      { key: 'educationalProgram', label: 'Освітня програма', sortable: true, width: '15%' },
      { key: 'groupSize', label: 'Розмір групи', sortable: true, width: '10%' },
      { key: 'note', label: 'Примітка', sortable: true, width: '15%' },
      { key: 'actions', label: 'Дії', sortable: false, width: '5%' }
    ],
    nutrition: [
      { key: 'name', label: 'Тип харчування', sortable: true, width: '15%' },
      { key: 'code', label: 'Код', sortable: true, width: '10%' },
      { key: 'description', label: 'Опис', sortable: true, width: '20%' },
      { key: 'characteristics', label: 'Характеристики', sortable: true, width: '15%' },
      { key: 'menu', label: 'Меню', sortable: true, width: '15%' },
      { key: 'restrictions', label: 'Обмеження', sortable: true, width: '15%' },
      { key: 'note', label: 'Примітка', sortable: true, width: '5%' },
      { key: 'actions', label: 'Дії', sortable: false, width: '5%' }
    ]
  };

  const columns = columnsConfig[activeTab];

  // Функция сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Сортированные данные
  const sortedData = useMemo(() => {
    const data = referenceData[activeTab];
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [referenceData, activeTab, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleCopyToClipboard = async (text, item) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item.id);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Помилка копіювання: ', err);
    }
  };

  const handleActionSelect = (actionId, item) => {
    if (actionId === 'view') {
      // Показать детальную информацию
      let message = '';
      if (activeTab === 'type') {
        message = `Тип тарифу: ${item.name}\nКод: ${item.code}\nОпис: ${item.description}\nХарактеристики: ${item.characteristics}\nЗастосування: ${item.usage}\nПримітка: ${item.note}`;
      } else if (activeTab === 'age') {
        message = `Вікова категорія: ${item.ageGroup}\nКод: ${item.code}\nОпис: ${item.description}\nХарактеристики: ${item.characteristics}\nОсвітня програма: ${item.educationalProgram}\nРозмір групи: ${item.groupSize}\nПримітка: ${item.note}`;
      } else if (activeTab === 'nutrition') {
        message = `Тип харчування: ${item.name}\nКод: ${item.code}\nОпис: ${item.description}\nХарактеристики: ${item.characteristics}\nМеню: ${item.menu}\nОбмеження: ${item.restrictions}\nПримітка: ${item.note}`;
      }
      alert(message);
    } else if (actionId === 'copy') {
      // Копировать код для использования в формах
      handleCopyToClipboard(item.code, item);
    }
    setActiveMenuIndex(null);
  };

  const ActionsMenu = ({ onActionSelect, onClose, item }) => {
    const actions = [
      {
        id: 'view',
        label: 'Переглянути деталі',
        icon: faInfoCircle
      },
      {
        id: 'copy',
        label: 'Копіювати код',
        icon: faCopy
      }
    ];

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, item)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  return (
    <main className="queuetable-main-content queuetable-page">
      <div className="container-fluid">
        <div className="queuetable-content-wrapper">
          {/* Панель табов */}
          <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="queuetable-label text-white-50">Довідник тарифів</span>
              <ul className="queuetable-year-tabs nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'type' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('type');
                      setSortConfig({ key: null, direction: 'asc' });
                    }}
                  >
                    Тип тарифу
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'age' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('age');
                      setSortConfig({ key: null, direction: 'asc' });
                    }}
                  >
                    Вікові категорії
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'nutrition' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('nutrition');
                      setSortConfig({ key: null, direction: 'asc' });
                    }}
                  >
                    Тип харчування
                  </button>
                </li>
              </ul>
            </div>
            
          </div>

          {/* Уведомление о копировании */}
          {copiedItem && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              Код успішно скопійовано в буфер обміну!
            </div>
          )}

          {/* Таблица */}
          <div className="card queuetable-table-card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table queuetable-table align-middle mb-0">
                  <thead>
                    <tr>
                      {columns.map((column) => (
                        <th 
                          key={column.key}
                          style={{ width: column.width }}
                          onClick={() => column.sortable && handleSort(column.key)}
                        >
                          <div className="d-flex align-items-center">
                            {column.label}
                            {column.sortable && (
                              <FontAwesomeIcon 
                                icon={getSortIcon(column.key)} 
                                className="ms-1" 
                                style={{ fontSize: '12px' }}
                              />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.map((item, index) => (
                      <tr key={item.id}>
                        {/* Динамическое отображение колонок в зависимости от вкладки */}
                        {columns.map((column) => {
                          if (column.key === 'actions') {
                            return (
                              <td key="actions" className="queuetable-actions text-end">
                                <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    className="btn btn-sm btn-link text-decoration-none p-0"
                                    onClick={() => handleMenuToggle(index)}
                                    style={{ color: '#0d6efd' }}
                                  >
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                  </button>
                                  
                                  {activeMenuIndex === index && (
                                    <ActionsMenu 
                                      onActionSelect={handleActionSelect}
                                      onClose={handleMenuClose}
                                      item={item}
                                    />
                                  )}
                                </div>
                              </td>
                            );
                          }
                          
                          return (
                            <td key={column.key}>
                              {item[column.key]}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Пагинация */}
              <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                <div className="queuetable-pagination-info">
                  Всього {sortedData.length} записів
                </div>
              </div>
            </div>
          </div>

          {/* Информационное сообщение */}
          
        </div>
      </div>
    </main>
  );
};

export default TariffsList;